import { type FC, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Tooltip, Typography } from 'shared/ui'
import { logger } from 'shared/lib'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { AiCallingApi } from 'entities/AICalling/api/aiCallingApi'
import { type Message } from 'entities/Message/model/Message'
import styles from './styles.module.scss'

type IProps = {
  message: Message
}

export const AnsweredByLabel: FC<IProps> = observer(({ message }) => {
  const { usersStore } = useEntitiesContext()
  const { answered_user, icon, source, source_id, answered_id } = message
  const [agentName, setAgentName] = useState<string | undefined>(undefined)
  const [agentLoading, setAgentLoading] = useState(false)

  if (icon !== 'icon_inbound_call') return null

  if (source === 'ai_calling_agent' && source_id && answered_id === 0) {
    const onMouseEnter = async () => {
      if (agentLoading || agentName) return
      try {
        setAgentLoading(true)
        const { data } = await AiCallingApi.getById(Number(source_id))
        setAgentName(data.name)
      } catch (err) {
        logger.error(err)
      } finally {
        setAgentLoading(false)
      }
    }

    return (
      <Tooltip label={agentName} ariaLabel={'answered-by'}>
        <Typography
          variant={'body-xs-regular'}
          ariaLabel={'answered-by'}
          color={'tertiary'}
          onMouseEnter={onMouseEnter}
        >
          Answered by AI Agent
        </Typography>
      </Tooltip>
    )
  }

  if (!answered_user) return null

  const currentUserId = usersStore.user?.id

  if (answered_user.id === currentUserId) {
    return (
      <Typography variant={'body-xs-regular'} ariaLabel={'answered-by'} color={'tertiary'}>
        Answered by you
      </Typography>
    )
  }

  const isRemoved = answered_user.disabled === true || answered_user.deleted_at !== null

  if (isRemoved) {
    if (answered_user.first_name) {
      return (
        <Tooltip
          label={`${answered_user.full_name}`}
          ariaLabel='answered-by'
          type='description'
          desc='No longer part of this workspace'
        >
          <Typography
            variant='body-xs-regular'
            ariaLabel='answered-by'
            color='tertiary'
            className={styles.nameWrap}
          >
            {`Answered by ${answered_user.first_name} (removed)`}
          </Typography>
        </Tooltip>
      )
    }

    return (
      <Tooltip label={'No longer part of this workspace'} ariaLabel={'answered-by'}>
        <Typography variant={'body-xs-regular'} ariaLabel={'answered-by'} color={'tertiary'}>
          Answered by a teammate
        </Typography>
      </Tooltip>
    )
  }

  return (
    <Tooltip label={answered_user.full_name} ariaLabel={'answered-by'}>
      <Typography
        variant={'body-xs-regular'}
        ariaLabel={'answered-by'}
        color={'tertiary'}
        className={styles.nameWrap}
      >
        {`Answered by ${answered_user.first_name}`}
      </Typography>
    </Tooltip>
  )
})
